import React from "react";
import {
    IonIcon,
    IonItem,
    IonLabel,
    IonNote,
    IonSpinner,
    IonToggle
} from "@ionic/react";

import {
    notificationsOutline
} from "ionicons/icons";

import {
    fantasyPushDisable,
    fantasyPushEnable,
    fantasyPushIsConfigured,
    fantasyPushIsEnabled,
    fantasyPushIsSupported,
    fantasyPushRegisterServiceWorker
} from "../services/fantasyPushNotifications";

import "./FantasyPushSettings.css";

function getPushErrorMessage(error: unknown): string {
    if (error instanceof Error) {
        return error.message;
    }

    return "No fue posible actualizar las notificaciones.";
}

export default function FantasyPushSettings() {

    const [loading, setLoading] =
        React.useState(true);

    const [enabled, setEnabled] =
        React.useState(false);

    const [message, setMessage] =
        React.useState("");

    const supported = fantasyPushIsSupported();

    const configured = fantasyPushIsConfigured();

    React.useEffect(
        function () {
            let componentMounted = true;

            async function loadStatus() {
                if (!supported) {
                    if (componentMounted) {
                        setLoading(false);
                    }

                    return;
                }

                try {
                    await fantasyPushRegisterServiceWorker();

                    const isEnabled =
                        await fantasyPushIsEnabled();

                    if (componentMounted) {
                        setEnabled(isEnabled);
                    }
                } catch (error: unknown) {
                    console.warn(
                        "No fue posible revisar las notificaciones:",
                        error
                    );
                } finally {
                    if (componentMounted) {
                        setLoading(false);
                    }
                }
            }

            loadStatus();

            return function () {
                componentMounted = false;
            };
        },
        [supported]
    );

    async function handleToggle(
        checked: boolean
    ): Promise<void> {
        if (loading) {
            return;
        }

        setMessage("");
        setLoading(true);

        try {
            if (checked) {
                await fantasyPushEnable();

                setEnabled(true);
                setMessage(
                    "Las notificaciones están activadas en este dispositivo."
                );
            } else {
                await fantasyPushDisable();

                setEnabled(false);
                setMessage(
                    "Las notificaciones fueron desactivadas en este dispositivo."
                );
            }
        } catch (error: unknown) {
            setMessage(
                getPushErrorMessage(error)
            );
        } finally {
            setLoading(false);
        }
    }

    if (!supported) {
        return (
            <section className="fantasy-push-settings">
                <IonItem lines="none">
                    <IonIcon
                        slot="start"
                        icon={notificationsOutline}
                    />

                    <IonLabel>
                        <h2>Notificaciones</h2>

                        <p>
                            Este navegador no admite
                            notificaciones Push.
                        </p>
                    </IonLabel>
                </IonItem>
            </section>
        );
    }

    if (!configured) {
        return (
            <section className="fantasy-push-settings">
                <IonItem lines="none">
                    <IonIcon
                        slot="start"
                        icon={notificationsOutline}
                    />

                    <IonLabel>
                        <h2>Notificaciones</h2>

                        <p>
                            Esta función estará disponible
                            próximamente.
                        </p>
                    </IonLabel>
                </IonItem>
            </section>
        );
    }

    return (
        <section className="fantasy-push-settings">
            <IonItem lines="none">
                <IonIcon
                    slot="start"
                    icon={notificationsOutline}
                />

                <IonLabel>
                    <h2>Notificaciones Fantasy</h2>

                    <p>
                        Recibe alertas de waivers, trades y
                        movimientos importantes de tu liga.
                    </p>
                </IonLabel>

                {loading ? (
                    <IonSpinner
                        slot="end"
                        name="crescent"
                    />
                ) : (
                    <IonToggle
                        slot="end"
                        checked={enabled}
                        onIonChange={function (event) {
                            handleToggle(
                                event.detail.checked
                            );
                        }}
                        aria-label="Activar notificaciones Fantasy"
                    />
                )}
            </IonItem>

            {message ? (
                <IonNote className="fantasy-push-settings__message">
                    {message}
                </IonNote>
            ) : null}
        </section>
    );
}
